import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { applicationService } from '@/lib/api/services/application'
import type {
  ApiResponse,
  PaginatedResponse,
  PaginationParams,
  Application,
  ApplicationStats,
  CreateApplication,
  UpdateApplication,
  UpdateApplicationStatus,
  ApplicationFilterParams
} from '@/lib/api/types'

// Query keys for caching
export const applicationKeys = {
  all: ['applications'] as const,
  lists: () => [...applicationKeys.all, 'list'] as const,
  list: (filters?: ApplicationFilterParams) => [...applicationKeys.lists(), filters] as const,
  details: () => [...applicationKeys.all, 'detail'] as const,
  detail: (id: string) => [...applicationKeys.details(), id] as const,
  filtered: (params: PaginationParams, filters: ApplicationFilterParams) =>
    [...applicationKeys.all, 'filtered', params, filters] as const,
  search: (query: string) => [...applicationKeys.all, 'search', query] as const,
  stats: () => [...applicationKeys.all, 'stats'] as const
}

// Hook to fetch all applications with pagination
export function useAllApplications(params: PaginationParams) {
  return useQuery<PaginatedResponse<Application>, Error>({
    queryKey: applicationKeys.filtered(params, {}),
    queryFn: () => applicationService.getAllApplications()
  })
}

// Hook to filter applications with pagination
export function useFilterApplications(
  params: PaginationParams = { page: 1, size: 10 },
  filters: ApplicationFilterParams = {},
  enabled: boolean
) {
  return useQuery<ApiResponse<PaginatedResponse<Application>>, Error>({
    queryKey: applicationKeys.filtered(params, filters),
    queryFn: () => applicationService.filterApplications(params, filters),
    enabled: enabled
  })
}

// Hook to fetch a single application
export function useApplication(applicationId?: string) {
  return useQuery<ApiResponse<Application>, Error>({
    queryKey: applicationKeys.detail(applicationId!),
    queryFn: () => applicationService.getApplicationById(applicationId!),
    enabled: !!applicationId
  })
}

// Hook to create a application
export function useCreateApplication() {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<Application>, Error, CreateApplication>({
    mutationFn: (data) => applicationService.createApplication(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: applicationKeys.lists() })
      queryClient.invalidateQueries({ queryKey: applicationKeys.all })
      queryClient.invalidateQueries({ queryKey: applicationKeys.stats() })
    }
  })
}

// Hook to update a application
export function useUpdateApplication() {
  const queryClient = useQueryClient()

  return useMutation<
    ApiResponse<Application>,
    Error,
    { applicationId: string; data: UpdateApplication }
  >({
    mutationFn: ({ applicationId, data }) =>
      applicationService.updateApplication(applicationId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: applicationKeys.lists() })
      queryClient.invalidateQueries({ queryKey: applicationKeys.all })
      queryClient.invalidateQueries({ queryKey: applicationKeys.detail(variables.applicationId) })
      queryClient.invalidateQueries({ queryKey: applicationKeys.stats() })
    }
  })
}

// Hook to update application status
export function useUpdateApplicationStatus() {
  const queryClient = useQueryClient()

  return useMutation<
    ApiResponse<null>,
    Error,
    { applicationId: string; data: UpdateApplicationStatus }
  >({
    mutationFn: ({ applicationId, data }) =>
      applicationService.updateApplicationStatus(applicationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: applicationKeys.lists() })
      queryClient.invalidateQueries({ queryKey: applicationKeys.all })
      queryClient.invalidateQueries({ queryKey: applicationKeys.stats() })
    }
  })
}

// Hook to delete a application
export function useDeleteApplication() {
  const queryClient = useQueryClient()

  return useMutation<
    ApiResponse<null>,
    Error,
    string
  >({
    mutationFn: (applicationId) =>
      applicationService.deleteApplication(applicationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: applicationKeys.lists() })
      queryClient.invalidateQueries({ queryKey: applicationKeys.all })
      queryClient.invalidateQueries({ queryKey: applicationKeys.stats() })
    }
  })
}

// Hook to search applications
export function useSearchApplications(
  searchBy?: string,
  enabled: boolean = true
) {
  return useQuery<ApiResponse<Application[]>, Error>({
    queryKey: applicationKeys.search(searchBy || ''),
    queryFn: () => applicationService.searchApplications(searchBy),
    enabled: enabled && !!searchBy && searchBy.length > 0,
    staleTime: 2 * 60 * 1000 // 2 minutes
  })
}

// Hook to fetch application stats
export function useApplicationStats() {
  return useQuery<ApplicationStats, Error>({
    queryKey: applicationKeys.stats(),
    queryFn: async () => {
      const response = await applicationService.getApplicationStats()
      return response.data
    },
    staleTime: 5 * 60 * 1000
  })
}

// Hook to fetch application statuses
export function useApplicationStatuses() {
  return useQuery<ApiResponse<Array<{ value: string; displayName: string }>>, Error>({
    queryKey: [...applicationKeys.all, 'applicationStatuses'],
    queryFn: () => applicationService.getApplicationStatuses(),
    staleTime: 30 * 60 * 1000 // 30 minutes - application statuses don't change often
  })
}

// Hook to fetch application types
export function useApplicationTypes() {
  return useQuery<ApiResponse<Array<{ value: string; displayName: string }>>, Error>({
    queryKey: [...applicationKeys.all, 'applicationTypes'],
    queryFn: () => applicationService.getApplicationTypes(),
    staleTime: 30 * 60 * 1000 // 30 minutes - application types don't change often
  })
}

